/**
 * Shared waiver-status helper for parent / client surfaces.
 *
 * Sachin 2026-06: "Some parents say they can't find the waiver." The
 * onboarding popup, the sticky bottom bar, and the nav pills all read
 * their state from here so the logic stays in ONE place.
 *
 * PRESENCE-based (any `signed_at` counts as signed) to match the
 * account-level popup gate in /auth/me. A waiver signed under an older
 * version still counts as signed so a parent is NEVER re-nagged once
 * they've signed — the user's explicit requirement. Version-currency
 * re-sign (new terms) is a separate, per-release concern handled by the
 * coach roster / audit tooling, not by these member nudges.
 *
 * Returns the people on the account who still need a signature:
 *   [{ kind: "self" | "athlete", key, athlete_id?, label }]
 */
export function getMissingWaivers(user) {
    if (!user) return [];
    // Kids in Athlete Mode can't sign — their parent signs from their own
    // login. Staff never sign member waivers.
    if (user.athlete_mode_for) return [];
    if (user.role === "owner" || user.role === "coach") return [];

    const out = [];
    const selfSigned = Boolean(user.self_waiver_signed || user.waiver?.signed_at);
    const firstName = (user.parent_first_name || (user.name || "").split(" ")[0] || "").trim();
    const athletes = user.athletes || [];
    // Adult-self accounts have a single "athlete" who IS the account holder.
    // Their one waiver is the self waiver — don't double-count the row.
    const isAdultSelf = athletes.length === 1
        && (athletes[0]?.first_name || "").trim() === firstName;

    if (!selfSigned) {
        out.push({
            kind: "self",
            key: "self",
            label: user.parent_first_name || user.name || "Account holder",
        });
    }
    if (!isAdultSelf) {
        athletes.forEach((a) => {
            const signed = Boolean(
                a.waiver_signed || a.waiver_signed_at || a.waiver?.signed_at,
            );
            if (signed) return;
            out.push({
                kind: "athlete",
                key: a.athlete_id,
                athlete_id: a.athlete_id,
                label: `${a.first_name || ""} ${a.last_name || ""}`.trim() || "Athlete",
            });
        });
    }
    return out;
}
